// src/components/navigation/navbar.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/logo";
import { dataNav } from "@/config/menu";
import { useBrandConfig } from "@/hooks/use-brand-config";
import {
  filterNavigationItems,
  isFeatureEnabled,
} from "@/utils/navigationFilter";
import { ThemeToggle } from "@/components/theme-toggle";
import { HelpCircle, Settings, Loader2, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { useAccounts } from "@/contexts/account-context";
import { useUser } from "@clerk/clerk-react";
import { useAccessLevel } from "@/hooks/use-access-level";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DEBUG = process.env.NODE_ENV === "development";

// Promotional Banner Component
const PromoBanner = ({ brandConfig }: { brandConfig: any }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { user } = useUser();
  const {
    isCommunityMember,
    hasUnlimitedAccess,
    isLoaded: accessLevelLoaded,
  } = useAccessLevel();

  // Anyone the access-level hook recognises as a paying/known member
  // (community members, customers, Aaron) sees no banner at all.
  const isKnownMember =
    accessLevelLoaded && (hasUnlimitedAccess || isCommunityMember);

  // Determine which banner to show based on membership status.
  // Known members get no banner; the community banner only shows if it's
  // explicitly enabled in config (currently off).
  const activeBanner = isKnownMember
    ? brandConfig?.communityBanner?.enabled
      ? brandConfig.communityBanner
      : null
    : brandConfig?.promoBanner;

  // Create unique banner key based on which banner is being shown
  const bannerType = isCommunityMember ? "community" : "promo";

  useEffect(() => {
    // Check if banner was dismissed
    const bannerKey = user?.id
      ? `${bannerType}-banner-dismissed-${user.id}`
      : `${bannerType}-banner-dismissed-anon`;
    const isDismissed = localStorage.getItem(bannerKey) === "true";

    if (!isDismissed) {
      setIsVisible(true);
    }
  }, [user, bannerType]);

  const handleDismiss = () => {
    setIsVisible(false);
    const bannerKey = user?.id
      ? `${bannerType}-banner-dismissed-${user.id}`
      : `${bannerType}-banner-dismissed-anon`;
    localStorage.setItem(bannerKey, "true");
  };

  // Check if any banner is enabled in brand config
  if (!activeBanner?.enabled || !isVisible) return null;

  // Different colors based on banner type
  const backgroundClass = isCommunityMember
    ? "bg-orange-600" // Solid orange for community
    : "bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-600"; // Purple to indigo for promo

  return (
    <div className={`${backgroundClass} text-white relative overflow-hidden`}>
      {/* Animated striped background pattern */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="h-full w-full animate-banner-stripes"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.3) 8px, rgba(255,255,255,0.3) 16px)",
            backgroundSize: "200% 200%",
          }}
        />
      </div>

      <div className="container mx-auto max-w-8xl px-4 sm:px-6 lg:px-8 relative">
        <Link
          to={activeBanner?.linkUrl || "#"}
          className="block py-3 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                <span className="font-bold text-base sm:text-lg">
                  {activeBanner?.title}
                </span>
              </div>
              <span className="hidden sm:inline text-base text-purple-100 font-medium">
                {activeBanner?.subtitle}
              </span>
            </div>

            {activeBanner?.dismissible && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDismiss();
                }}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

// Hook to safely use campaign data context
function useCampaignDataSafely() {
  try {
    // Dynamically import to avoid errors when context doesn't exist
    const { useCampaignData } = require("@/contexts/campaign-data");
    return useCampaignData();
  } catch {
    // Return safe defaults when CampaignDataProvider is not available
    return {
      loadingStatus: "idle",
      isBackgroundRefreshing: false,
      backgroundRefreshingAccounts: [],
    };
  }
}

interface NavbarProps {
  // No props needed now
}

export function Navbar({}: NavbarProps) {
  const location = useLocation();

  // Safely use accounts context - handle case where provider isn't available
  let accounts: any[] = [],
    activeDexieId: string | null = null,
    switchAccount = async (_newDexieId: string) => {},
    activeAccount: any = undefined;
  try {
    const accountsData = useAccounts();
    ({ accounts, activeDexieId, switchAccount, activeAccount } = accountsData);
  } catch (err) {
    // AccountProvider not available (e.g., on sales routes) - use safe defaults
    console.log("Navbar: AccountProvider not available, using defaults");
  }
  const campaignData = useCampaignDataSafely();
  const {
    loadingStatus,
    isBackgroundRefreshing,
    backgroundRefreshingAccounts,
  } = campaignData;
  const isLoading = loadingStatus !== "idle";

  // Use the brand config hook instead of static import
  const { brandConfig } = useBrandConfig();
  const { isCommunityMember, isLoaded: accessLevelLoaded } = useAccessLevel();

  const handleAccountSwitch = async (newDexieId: string) => {
    try {
      console.log("Navbar account switch:", {
        current: activeDexieId,
        new: newDexieId,
      });
      await switchAccount(newDexieId);
    } catch (err) {
      console.error("Account switch failed:", err);
    }
  };

  // Filter navigation items based on brand configuration AND current account's business mode
  const filteredDataNav = filterNavigationItems(
    dataNav,
    brandConfig,
    activeAccount,
  );

  // Show account dropdown when there are multiple accounts available
  const showAccountDropdown = accounts && accounts.length > 1;

  return (
    <>
      <PromoBanner brandConfig={brandConfig} />
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-8xl px-4 sm:px-6 lg:px-8 flex h-14 items-center">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="mr-4 flex items-center space-x-2 transition-all duration-200 hover:opacity-80"
            >
              {brandConfig?.logo ? (
                <img
                  src={brandConfig.logo.url}
                  alt={brandConfig.logo.altText}
                  width={brandConfig.logo.width}
                  height={brandConfig.logo.height}
                  className="object-contain"
                />
              ) : (
                <Logo /> // Fallback to default logo if none in config
              )}

              {(brandConfig?.nav?.showTitle ?? true) && (
                <span className="hidden font-bold sm:inline-block">
                  {brandConfig?.nav?.title || brandConfig.appName}
                </span>
              )}
            </Link>

            {/* Account Selector - show when multiple accounts available */}
            {showAccountDropdown && (
              <Select
                value={activeDexieId || ""}
                onValueChange={handleAccountSwitch}
                disabled={isLoading}
              >
                <SelectTrigger
                  className="w-[200px]"
                  aria-label="Account selector"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Switching...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 w-full">
                      <SelectValue placeholder="Select account" />
                      {isBackgroundRefreshing && (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center">
                                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground opacity-60" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Refreshing data for{" "}
                                {backgroundRefreshingAccounts.length} account
                                {backgroundRefreshingAccounts.length !== 1
                                  ? "s"
                                  : ""}{" "}
                                in background
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem
                      key={account.dexieId}
                      value={account.dexieId}
                      className="flex items-center gap-2"
                    >
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Navigation items using NavigationMenu for list layout */}
            {/* and DropdownMenu for dropdowns */}
            <NavigationMenu>
              <NavigationMenuList>
                {filteredDataNav.map((item) => {
                  if (item.devOnly && !DEBUG) {
                    return null;
                  }

                  // Use DropdownMenu for items with children
                  if (item.items) {
                    return (
                      <NavigationMenuItem key={item.title}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            {/* Mimic NavigationMenuTrigger styling */}
                            <Button
                              variant="ghost"
                              className="h-9 px-4 py-2 flex items-center gap-1"
                            >
                              {item.title}
                              <ChevronDown className="relative top-[1px] ml-1 h-3 w-3 transition duration-300 group-data-[state=open]:rotate-180" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {item.items.map((subItem) => (
                              <DropdownMenuItem key={subItem.title} asChild>
                                <Link
                                  to={subItem.href || "#"}
                                  className={cn(
                                    "w-full block", // Ensure link takes full width
                                    location.pathname === subItem.href &&
                                      "bg-accent",
                                  )}
                                >
                                  {subItem.title}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </NavigationMenuItem>
                    );
                  }

                  // Render Standard Link Item using NavigationMenuLink
                  return (
                    <NavigationMenuItem key={item.title}>
                      <NavigationMenuLink asChild>
                        {/* Use external link for community members, internal Link for others */}
                        {item.title === "Membership" &&
                        accessLevelLoaded &&
                        isCommunityMember ? (
                          <a
                            href="https://mikerhodes.circle.so/c/ai-questions/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              navigationMenuTriggerStyle(),
                              "font-bold text-primary",
                            )}
                          >
                            Login to Ads To AI
                          </a>
                        ) : (
                          <Link
                            to={item.href || "#"}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              location.pathname === item.href
                                ? "bg-accent text-accent-foreground"
                                : "",
                              item.title === "Membership"
                                ? "font-bold text-primary"
                                : "",
                            )}
                          >
                            {item.title}
                          </Link>
                        )}
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="flex items-center space-x-4 ml-auto">
            {/* Theme toggle - works with brand customization */}
            <ThemeToggle />

            {/* Settings icon - conditionally show based on brand config */}
            {brandConfig?.navigation?.accountDropdown?.showSettings && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/settings"
                      className={cn(
                        "flex items-center justify-center h-8 w-8 rounded-md transition-all duration-200",
                        "hover:translate-y-[-1px] active:translate-y-[1px]",
                        location.pathname === "/settings"
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent/50 text-foreground/60",
                      )}
                    >
                      <Settings className="h-5 w-5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Settings</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Help & Feedback icon - conditionally show based on brand config */}
            {brandConfig?.support?.showFeedback && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/help"
                      className={cn(
                        "flex items-center justify-center h-8 w-8 rounded-md transition-all duration-200",
                        "hover:translate-y-[-1px] active:translate-y-[1px]",
                        location.pathname === "/help"
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent/50 text-foreground/60",
                      )}
                    >
                      <HelpCircle className="h-5 w-5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Help & Feedback</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Auth buttons */}
            <SignedOut>
              <Button variant="outline" asChild>
                <Link to="/sign-in">Sign In</Link>
              </Button>
            </SignedOut>
            <div className="relative z-9999 h-1">
              <SignedIn>
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: "w-8 h-8 flex items-center justify-center",
                    },
                  }}
                />
              </SignedIn>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
